import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { AllocationMember, MonthlyAllocation } from '../types/allocation';
import { ProjectCost } from '../types/api';
import { getMemberMonthlyCost } from '../utils/costUtils';

interface ChartsProps {
  allocationMembers: AllocationMember[];
  months: MonthlyAllocation[];
  projectCosts: ProjectCost[];
  endDate?: string;
}

const COLORS = ['#3b82f6', '#22c55e', '#a855f7', '#ef4444', '#f59e0b', '#14b8a6'];

const Charts: React.FC<ChartsProps> = ({ allocationMembers, months, projectCosts, endDate }) => {
  // Derive chart series from inputs; memoized to avoid recomputation on unrelated renders
  const { employeeTotal, pieData, barData } = useMemo(() => {
    // Calculate project duration in months
    const calculateProjectDurationInMonths = () => {
      // If no months array, return 1 as fallback
      if (months.length === 0) return 1;
      
      // If endDate is provided, calculate duration from start to end
      if (endDate) {
        const startDate = new Date(months[0].monthStart);
        const endDateObj = new Date(endDate);
        const diffTime = Math.abs(endDateObj.getTime() - startDate.getTime());
        const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30.44));
        return Math.max(1, diffMonths);
      }
      
      // If no endDate, use the number of months in the months array
      // This ensures project costs are calculated for all displayed months
      return months.length;
    };

    const durationInMonths = calculateProjectDurationInMonths();
    // Calculate billable costs (sum all monthly costs)
    const billableCosts = projectCosts.filter(cost => cost.billable).reduce((total, cost) => {
      const costTotal = Object.values(cost.monthlyCosts || {}).reduce((sum, val) => sum + (val || 0), 0);
      return total + costTotal;
    }, 0);
    // Calculate non-billable costs (sum all monthly costs)
    const nonBillableCosts = projectCosts.filter(cost => !cost.billable).reduce((total, cost) => {
      const costTotal = Object.values(cost.monthlyCosts || {}).reduce((sum, val) => sum + (val || 0), 0);
      return total + costTotal;
    }, 0);
    // Aggregate total employee cost across all weeks
    let employeeTotalCost = 0;
    allocationMembers.forEach(m => {
      Object.keys(m.monthlyAllocations).forEach((monthStart) => {
        employeeTotalCost += getMemberMonthlyCost(m, undefined, monthStart);
      });
    });

    // Pie series: Employee vs Billable vs Non-billable (overall breakdown)
    const pie = [
      { name: 'Employee Costs', value: employeeTotalCost },
      { name: 'Billable Costs', value: billableCosts },
      { name: 'Non-billable Costs', value: nonBillableCosts },
    ].filter(item => item.value > 0);

    // Bar series: per-month Hours and Total Cost (employee + project costs for that month)
    const bar = months.map((m) => {
      let monthHours = 0;
      let monthEmployeeCost = 0;
      allocationMembers.forEach((member) => {
        const hours = member.monthlyAllocations[m.monthStart] || 0;
        monthHours += hours;
        monthEmployeeCost += getMemberMonthlyCost(member, undefined, m.monthStart);
      });
      // Sum project costs for this specific month
      const monthlyProjectCosts = projectCosts.reduce((total, cost) => {
        return total + (cost.monthlyCosts?.[m.monthStart] || 0);
      }, 0);
      const monthTotal = monthEmployeeCost + monthlyProjectCosts;
      return {
        month: m.label,
        hours: Number(monthHours.toFixed(2)),
        employeeCost: Number(monthEmployeeCost.toFixed(2)),
        totalCost: Number(monthTotal.toFixed(2)),
      };
    });

    return { employeeTotal: employeeTotalCost, pieData: pie, barData: bar };
  }, [allocationMembers, months, projectCosts, endDate]);

  if (allocationMembers.length === 0 || months.length === 0) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow p-6 border border-gray-200/50">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Charts & Reporting</h3>
        <p className="text-sm text-gray-500">Add members and allocations to view charts.</p>
      </div>
    );
  }

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow p-6 border border-gray-200/50">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900">Charts & Reporting</h3>
        <p className="mt-1 text-sm text-gray-600">Visual breakdown of costs and weekly hours.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart: Overall cost breakdown */}
        <div className="h-80">
          <h4 className="text-md font-medium text-gray-900 mb-2">Cost Breakdown</h4>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip formatter={(value: any) => `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bar Chart: Monthly hours and apportioned total cost */}
        <div className="h-80">
          <h4 className="text-md font-medium text-gray-900 mb-2">Monthly Hours & Cost</h4>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis yAxisId="left" orientation="left" stroke="#6b7280" />
              <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" tickFormatter={(v) => `₹${v}`} />
              <RechartsTooltip formatter={(value: any, name: any) => name?.toString().toLowerCase().includes('hours') ? `${value} hrs` : `₹${Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
              <Legend />
              <Bar yAxisId="left" dataKey="hours" name="Hours" fill="#94a3b8" />
              <Bar yAxisId="right" dataKey="totalCost" name="Total Cost" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Charts;
